function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { formatMoney } from '@shopify/theme-currency';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import Debug from 'scripts/libs/debug';
var PRICE_ATTRIBUTE_CANDIDATES = ['price', 'priceAdjustment', 'priceAdjust', 'priceAddOn', 'priceAddon', 'addPrice', 'extraPrice', 'optionPrice', 'avisPrice', 'avisOptionPrice', 'amount'];
class PropertyPricingSync {
  constructor(iamProductForm) {
    this.iamForm = iamProductForm;
    this.syncDisplayedPrice = this.syncDisplayedPrice.bind(this);
    this.onDisplayedPriceMutated = this.onDisplayedPriceMutated.bind(this);
    this.displayedPriceObserver = null;
    iamotaEventHandler.subscribe('productForm:property:changed', this.syncDisplayedPrice, {
      context: this.iamForm,
      source: "product-form--property-pricing.js"
    });
    iamotaEventHandler.subscribe('productForm:variant:changed', this.syncDisplayedPrice, {
      context: this.iamForm,
      source: "product-form--property-pricing.js",
      priority: 110
    });
    iamotaEventHandler.subscribe('productForm:price:updated', this.syncDisplayedPrice, {
      context: this.iamForm,
      source: "product-form--property-pricing.js"
    });
    this.syncDisplayedPrice();
    this.observeDisplayedPrice();
  }

  // The NON-LOYALTY price currently shown in the sink (#ProductPrice) — i.e. the
  // base + add-on total, which Avis authors. We read it straight from the sink
  // rather than iamForm.getCurrentDisplayPrice(), because that now resolves to
  // the loyalty overlay (a lower number); using it here would zero out the
  // inferred Avis surcharge. The loyalty overlay is layered on top of this value
  // separately (product-form--loyalty-price.js).
  getSinkDisplayedPrice(priceContainer) {
    var _this$iamForm$parseDi, _this$iamForm;
    var el = priceContainer === null || priceContainer === void 0 ? void 0 : priceContainer.querySelector('[data-product-price]');
    if (!el) return null;
    var parsed = (_this$iamForm$parseDi = (_this$iamForm = this.iamForm).parseDisplayedMoney) === null || _this$iamForm$parseDi === void 0 ? void 0 : _this$iamForm$parseDi.call(_this$iamForm, el.textContent);
    if (Number.isFinite(parsed)) return parsed;
    var cents = Number.parseInt(el.dataset.priceCents, 10);
    return Number.isFinite(cents) ? cents : null;
  }
  observeDisplayedPrice() {
    var _this$displayedPriceO;
    (_this$displayedPriceO = this.displayedPriceObserver) === null || _this$displayedPriceO === void 0 || _this$displayedPriceO.disconnect();
    var priceContainer = this.iamForm.querySelector(PropertyPricingSync.THEME_PRICE_CONTAINER_SELECTOR) || this.iamForm.querySelector('iam-product-price');
    if (!priceContainer) return;
    this.displayedPriceObserver = new MutationObserver(this.onDisplayedPriceMutated);
    this.displayedPriceObserver.observe(priceContainer, {
      characterData: true,
      childList: true,
      subtree: true
    });
  }
  onDisplayedPriceMutated(mutations) {
    var productPrice = mutations.find(_ref => {
      var _element$closest;
      var {
        target
      } = _ref;
      var element = target.nodeType === Node.TEXT_NODE ? target.parentElement : target;
      return element === null || element === void 0 || (_element$closest = element.closest) === null || _element$closest === void 0 ? void 0 : _element$closest.call(element, '[data-product-price]');
    });
    if (!productPrice) return;
    var variant = this.iamForm.getCurrentVariant();
    var priceContainer = this.iamForm.querySelector(PropertyPricingSync.THEME_PRICE_CONTAINER_SELECTOR) || this.iamForm.querySelector('iam-product-price');
    if (!variant || !priceContainer) return;
    var displayedPrice = this.getSinkDisplayedPrice(priceContainer);
    if (!Number.isFinite(displayedPrice)) return;
    var effectiveSurcharge = displayedPrice - variant.price;
    var storedSurcharge = Number.parseInt(priceContainer.dataset.currentSurcharge, 10) || 0;
    if (effectiveSurcharge === storedSurcharge) return;
    priceContainer.dataset.currentSurcharge = effectiveSurcharge;
    priceContainer.dataset.currentPrice = displayedPrice;
    this.applyComparePrice(priceContainer, effectiveSurcharge, variant);
  }
  applyComparePrice(priceContainer, surcharge, variant) {
    var productPrice = priceContainer.querySelector('[data-product-price]');
    var currentPrice = variant.price + surcharge;
    var currentComparePrice = variant.compare_at_price ? variant.compare_at_price + surcharge : null;
    var isOnSale = Boolean(currentComparePrice && currentComparePrice > currentPrice);
    var formattedComparePrice = isOnSale ? formatMoney(currentComparePrice, window.theme.settingCurrencyFormat || '{{amount}}') : '';
    var existingCompareElements = Array.from(new Set([...priceContainer.querySelectorAll('[data-compare-price], .product-price__compare'), ...this.iamForm.querySelectorAll('[data-compare-price], .product-price__compare')]));
    if (isOnSale) {
      var comparePrices = existingCompareElements.length ? existingCompareElements : [this.createComparePrice(productPrice)].filter(Boolean);
      comparePrices.forEach(element => {
        element.innerHTML = formattedComparePrice;
        element.dataset.priceCents = currentComparePrice;
        element.hidden = false;
      });
    } else {
      existingCompareElements.forEach(element => {
        element.innerHTML = '';
        element.hidden = true;
      });
    }
  }
  syncDisplayedPrice() {
    var variant = this.iamForm.getCurrentVariant();
    var priceContainer = this.iamForm.querySelector(PropertyPricingSync.THEME_PRICE_CONTAINER_SELECTOR) || this.iamForm.querySelector('iam-product-price');
    var productPrice = priceContainer === null || priceContainer === void 0 ? void 0 : priceContainer.querySelector('[data-product-price]');
    if (!variant || !priceContainer || !productPrice) return;
    var parsedSurcharge = this.getSelectedSurcharge();
    var displayedPrice = this.getSinkDisplayedPrice(priceContainer);
    var inferredSurcharge = Number.isFinite(displayedPrice) && displayedPrice > variant.price ? displayedPrice - variant.price : 0;
    var surcharge = Math.max(parsedSurcharge, inferredSurcharge);
    var currentPrice = variant.price + surcharge;
    var currentComparePrice = variant.compare_at_price ? variant.compare_at_price + surcharge : null;
    var isOnSale = Boolean(currentComparePrice && currentComparePrice > currentPrice);
    if (parsedSurcharge >= inferredSurcharge) {
      productPrice.innerHTML = formatMoney(currentPrice, window.theme.settingCurrencyFormat || '{{amount}}');
    }
    productPrice.dataset.priceCents = currentPrice;
    productPrice.style.color = isOnSale ? 'var(--sale-color)' : 'var(--text-color)';
    priceContainer.dataset.currentPrice = currentPrice;
    priceContainer.dataset.currentSurcharge = surcharge;
    this.applyComparePrice(priceContainer, surcharge, variant);
    Debug.details('[property-pricing] syncDisplayedPrice', {
      variantId: variant.id,
      basePrice: variant.price,
      baseComparePrice: variant.compare_at_price,
      parsedSurcharge,
      inferredSurcharge,
      surcharge,
      currentPrice,
      currentComparePrice,
      isOnSale
    });
  }
  getSelectedSurcharge() {
    var surcharge = this.iamForm.getPropertyInputs().reduce((total, input) => total + this.getInputSurcharge(input), 0);
    Debug.verbose('[property-pricing] getSelectedSurcharge', {
      surcharge,
      propertyInputs: this.iamForm.getPropertyInputs().map(input => ({
        name: input.name,
        value: input.value,
        type: input.type,
        checked: input.checked,
        id: input.id
      }))
    });
    return surcharge;
  }
  createComparePrice(productPrice) {
    var comparePrice = document.createElement('s');
    var insertionTarget = (productPrice === null || productPrice === void 0 ? void 0 : productPrice.parentElement) || productPrice;
    if (!(insertionTarget !== null && insertionTarget !== void 0 && insertionTarget.parentElement)) return null;
    comparePrice.className = 'product-price__compare price-item price-item--regular';
    comparePrice.dataset.comparePrice = '';
    insertionTarget.insertAdjacentElement('beforebegin', comparePrice);
    return comparePrice;
  }
  getInputSurcharge(input) {
    if (!input || input.disabled) return 0;
    var tagName = input.tagName.toLowerCase();
    var type = (input.getAttribute('type') || '').toLowerCase();
    if ((type === 'radio' || type === 'checkbox') && !input.checked) return 0;
    if (tagName === 'select') {
      return Array.from(input.selectedOptions || []).reduce((total, option) => total + this.getElementPriceAdjustment(option), 0);
    }
    return this.getElementPriceAdjustment(input);
  }
  getElementPriceAdjustment(element) {
    var candidates = [element, this.getAssociatedLabel(element)].filter(Boolean);
    for (var candidate of candidates) {
      var adjustmentFromData = this.getPriceFromDataAttributes(candidate);
      if (adjustmentFromData !== null) return adjustmentFromData;
    }
    for (var _candidate of candidates) {
      var adjustmentFromText = this.getPriceFromText(_candidate.textContent);
      if (adjustmentFromText !== null) return adjustmentFromText;
    }
    return 0;
  }
  getAssociatedLabel(element) {
    var _this$iamForm$propert;
    if (!(element !== null && element !== void 0 && element.id)) return (element === null || element === void 0 ? void 0 : element.closest('label')) || (element === null || element === void 0 ? void 0 : element.nextElementSibling);
    return ((_this$iamForm$propert = this.iamForm.propertyScope) === null || _this$iamForm$propert === void 0 ? void 0 : _this$iamForm$propert.querySelector("label[for=\"".concat(element.id, "\"]"))) || element.closest('label') || element.nextElementSibling;
  }
  getPriceFromDataAttributes(element) {
    if (!(element !== null && element !== void 0 && element.dataset)) return null;
    for (var attribute of PRICE_ATTRIBUTE_CANDIDATES) {
      var value = element.dataset[attribute];
      var parsed = this.parseExplicitAmount(value);
      if (parsed !== null) return parsed;
    }
    return null;
  }
  getPriceFromText() {
    var text = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    if (typeof text !== 'string' || !text.trim()) return null;
    var explicitChangeMatch = text.match(/([+-])\s*([$€£¥₹฿₫])?\s*([\d.,']+)/) || text.match(/([$€£¥₹฿₫])\s*([+-])\s*([\d.,']+)/);
    if (!explicitChangeMatch) return null;
    var sign = explicitChangeMatch[1] === '+' || explicitChangeMatch[2] === '+' ? 1 : -1;
    var amountSource = explicitChangeMatch[3] || explicitChangeMatch[2];
    var amount = this.parseMoneyToCents(amountSource);
    return amount === null ? null : sign * amount;
  }
  parseExplicitAmount(value) {
    if (value == null || value === '') return null;
    var normalizedValue = String(value).trim();
    if (!/[+\-.,'$€£¥₹฿₫]/.test(normalizedValue)) return null;
    return this.parseMoneyToCents(normalizedValue);
  }
  parseMoneyToCents(value) {
    var _value$match, _separatorMatches;
    if (typeof value !== 'string') return null;
    var numericChunk = (_value$match = value.match(/[-+]?[\d\s.,']+/)) === null || _value$match === void 0 || (_value$match = _value$match[0]) === null || _value$match === void 0 ? void 0 : _value$match.trim();
    if (!numericChunk) return null;
    var normalizedChunk = numericChunk.replace(/[+\s']/g, '');
    var separatorMatches = Array.from(normalizedChunk.matchAll(/[.,]/g));
    if (!separatorMatches.length) {
      var _wholeValue = Number.parseInt(normalizedChunk, 10);
      return Number.isNaN(_wholeValue) ? null : _wholeValue * 100;
    }
    var lastSeparatorIndex = (_separatorMatches = separatorMatches[separatorMatches.length - 1]) === null || _separatorMatches === void 0 ? void 0 : _separatorMatches.index;
    var fractionLength = normalizedChunk.length - lastSeparatorIndex - 1;
    if (fractionLength > 0 && fractionLength <= 2) {
      var wholePart = normalizedChunk.slice(0, lastSeparatorIndex).replace(/[.,]/g, '');
      var fractionalPart = normalizedChunk.slice(lastSeparatorIndex + 1).replace(/[.,]/g, '').padEnd(2, '0').slice(0, 2);
      var cents = Number.parseInt("".concat(wholePart || '0').concat(fractionalPart), 10);
      return Number.isNaN(cents) ? null : cents;
    }
    var wholeValue = Number.parseInt(normalizedChunk.replace(/[.,]/g, ''), 10);
    return Number.isNaN(wholeValue) ? null : wholeValue * 100;
  }
}
_defineProperty(PropertyPricingSync, "THEME_PRICE_CONTAINER_SELECTOR", '[data-product-pricing="theme"], [data-product-pricing]:not(.apo-variant-price)');
iamotaEventHandler.subscribe('productForm:init', _ref2 => {
  var {
    iamForm
  } = _ref2;
  new PropertyPricingSync(iamForm);
}, {
  source: "product-form--property-pricing.js"
});